import axios from 'axios';

const BACKEND_BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;
const API = axios.create({ baseURL: `${BACKEND_BASE_URL}/api/v1/user` });
const publicAPI = axios.create({ baseURL: `${BACKEND_BASE_URL}/api/v1/user` });

// Request interceptor to add authorization and accept headers
API.interceptors.request.use(
  (req) => {
    const userToken = localStorage.getItem('token');
    if (userToken) {
        req.headers.Authorization = `Bearer ${userToken}`;
        req.headers.Accept = 'application/json';
    }
    return req;
  },
  (error) => {
    // console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor to handle responses and errors
API.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response) {
      // Server responded with a status other than 2xx
      if (error.response.status === 401) {
        // Handle unauthorized access
        // console.error('Unauthorized access - possibly due to expired token');
        // Optionally, redirect to login page or refresh token
      } else {
        // Handle other response errors
        // console.error('Response error:', error.response.data.message || 'An error occurred');
      }
    } else if (error.request) {
      // Request was made but no response received
      // console.error('No response received:', error.request);
    } else {
      // Something else caused the error
      // console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

// No auth headers added
publicAPI.interceptors.response.use(
  (response) => response,
  (error) => {
    // console.error('Public API Error:', error.message);
    return Promise.reject(error);
  }
);





// API Endpoints

// Supported Countries
export const getAllowedCountries = () => API.get('/countries/supported');
export const getCountryBanks = (countryCode) => API.get(`/countries/${countryCode}/banks`);
export const getCountryStates = (countryCode) => API.get(`/countries/${countryCode}/states`);
export const allowedTransferMethods = (countryCode) => API.get(`/countries/${countryCode}/available-transfer-methods`);
export const getRecipientDetails = (credentials) => API.get(`/user/wallets/transfer/retrieve-recipient?recipient_info=${credentials}`);

// User Auth
export const getAuthenticatedUser = () => API.get('/user/dashboard');
export const verifyEmail = (email) => API.post('/auth/email/validate', { email });
export const resendEmailOtp = (sessionId) => API.get(`/auth/email/resend-otp?session_id=${sessionId}`);
export const verifyEmailOtp = (sessionId, otpCode) => API.post('/auth/email/verify-otp', { session_id: sessionId, otp_code: otpCode});

export const registerUser = (userData) => API.post('/auth/register', userData);
export const loginUser = (credentials) => API.post('/auth/login', credentials);
export const resendLoginOtp = (sessionId) => API.get(`/auth/login/otp/resend?session_id=${sessionId}`);
export const verifyLoginOtp = (sessionId, otpCode) => API.post('/auth/login/otp/verify', { session_id: sessionId, otp_code: otpCode});
export const logoutUser = () => API.get('/auth/logout');

// export const updateUserProfile = (profileData) => API.put('/auth/user/profile', profileData);

export const passwordReset = (email) => API.get(`/auth/password-reset/send-otp?email=${email}`);
export const resendPasswordOtp = (sessionId) => API.get(`/auth/password-reset/resend-otp?session_id=${sessionId}`);
export const changePassword = (passwordData) => API.post('/auth/password-reset/submit', passwordData);

// KYC
export const kycInitiated = (credentials) => API.post('/user/settings/initiate-kyc', credentials);
export const updateKycDetails = (credentials) => API.post('/user/settings/update-kyc-details', credentials);

// Wallets & Currency
export const getAllWallets = () => API.get('/user/wallets');
export const addWallets = (currencyID) => API.post(`/user/wallets/create?currency_id=${currencyID}`);
export const getAllCurrencies = () => API.get('/currencies');
export const currencyConversionPair = () => API.get('/currencies/pairs');

// Funding
export const getPaymentGateways = (walletRefId) => API.get(`/user/wallets/${walletRefId}/funding-payment-gateways`);
export const swapCurrency = (walletRefId, credentials) => API.post(`/user/wallets/${walletRefId}/swap`, credentials);
export const transferFunds = (walletRefId, credentials) => API.post(`/user/wallets/${walletRefId}/p2p-transfer`, credentials);
export const transferFundsSummary = (walletRefId, credentials) => API.get(`/user/wallets/${walletRefId}/p2p-transfer-summary?sending_amount=${credentials.amount}&receiving_currency_id=${credentials.receivingCurrencyId}&receiving_acct_id=${credentials.receivingAccountId}`);
export const initiateFunding = (walletRefId, credentials) => API.post(`/user/wallets/${walletRefId}/initiate-funding`, credentials);
export const updateTransactionProcess = (ref_id, credentials) => API.post(`/user/transactions/${ref_id}/update`, credentials);
export const updateCanceledTransactionProcess = (ref_id, credentials) => API.post(`/user/transactions/${ref_id}/cancel`, credentials);
export const verifyTransaction = (ref_id) => API.post(`/user/transactions/${ref_id}/verify`);
export const getTransactions = (itemsPerPage, pageNumber) => API.get(`user/transactions?itemsPerPage=${itemsPerPage}&page=${pageNumber}`);
export const getTransactionDetails = (refId) => API.get(`user/transactions/${refId}/details`);

// User profile settings
export const updateProfile = (credentials) => API.post('/user/settings/update-profile', credentials);
export const updateTransactionPin = (credentials) => API.post('/user/settings/update-transfer-pin', credentials);

// Contact feedback
export const sendFeedback = (credentials) => API.post('/send-contact', credentials);

// Virtual Cards
export const getVirtualCards = (filters = {}) => API.get('/virtual-cards', { params: filters });
export const getVirtualCardProducts = () => API.get('/virtual-cards/products');
export const initiateVirtualCardCreation = (credentials) => API.post('/virtual-cards/create', credentials);
export const validateVirtualCardCreation = (credentials) => API.post('/virtual-cards/validate', credentials);
export const validateVirtualCardFunding = (virtualCardId, credentials) => API.post(`/virtual-cards/${virtualCardId}/fund/validate`, credentials);
export const initiateVirtualCardFunding = (virtualCardId, credentials) => API.post(`/virtual-cards/${virtualCardId}/fund/initiate`, credentials);
export const validateVirtualCardWithdrawal = (virtualCardId, credentials) => API.post(`/virtual-cards/${virtualCardId}/withdrawal/validate`, credentials);
export const initiateVirtualCardWithdrawal = (virtualCardId, credentials) => API.post(`/virtual-cards/${virtualCardId}/withdrawal/initiate`, credentials);
export const getAllVirtualCardActivities = (filters = {}) => API.get('/virtual-cards/activity-logs', { params: filters });
export const getVirtualCardActivities = (virtualCardId, filters = {}) => API.get(`/virtual-cards/${virtualCardId}/activity-logs`, { params: filters });
export const verifyVirtualCardActivity = (virtualCardActivityId) => API.get(`/virtual-cards/activity-logs/${virtualCardActivityId}/verify`);
export const getVirtualCardDetails = (virtualCardId) => API.get(`/virtual-cards/${virtualCardId}/retrieve`);
export const getVirtualCardSensitiveData = (virtualCardId, payload) => API.post(`/virtual-cards/${virtualCardId}/retrieve-sensitive`, payload);
export const blockVirtualCard = (virtualCardId, payload) => API.post(`/virtual-cards/${virtualCardId}/block`, payload);
export const unblockVirtualCard = (virtualCardId, payload) => API.post(`/virtual-cards/${virtualCardId}/unblock`, payload);
export const validateVirtualCardTermination = (virtualCardId, credentials) => API.post(`/virtual-cards/${virtualCardId}/terminate/validate`, credentials);
export const initiateVirtualCardTermination = (virtualCardId, credentials) => API.post(`/virtual-cards/${virtualCardId}/terminate/initiate`, credentials);

export default API;
